import type React from "react";
import classNames from "classnames";
import {
    CheckGlyph,
    IndeterminateGlyph,
} from "@design-system/Checkbox/CheckboxGlyphs";
import type {
    AnatomySchematic,
    ResolvedAnatomyBinding,
} from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import {
    AvatarSchematic,
    type AvatarSchematicContent,
} from "./ComponentAnatomySchematicAvatar";
import { BreadcrumbsSchematic } from "./ComponentAnatomySchematicBreadcrumbs";
import { ButtonSchematic } from "./ComponentAnatomySchematicButton";
import {
    FieldSchematic,
    type FieldSchematicContent,
} from "./ComponentAnatomySchematicField";
import {
    InputSchematic,
    type InputSchematicContent,
} from "./ComponentAnatomySchematicInput";
import {
    KickLogoSchematic,
    type KickLogoSchematicContent,
} from "./ComponentAnatomySchematicKickLogo";
import { LinkSchematic } from "./ComponentAnatomySchematicLink";
import { MenuSchematic } from "./ComponentAnatomySchematicMenu";
import { OTPFieldSchematic } from "./ComponentAnatomySchematicOTPField";
import {
    TextAreaSchematic,
    type TextAreaSchematicContent,
} from "./ComponentAnatomySchematicTextArea";
import {
    PickerSchematic,
    type PickerSchematicContent,
} from "./ComponentAnatomySchematicPicker";
import { ShortcutSchematic } from "./ComponentAnatomySchematicShortcut";
import { SideNavSchematic } from "./ComponentAnatomySchematicSideNav";
import { TooltipSchematic } from "./ComponentAnatomySchematicTooltip";
import {
    SwitchSchematic,
    type SwitchSchematicContent,
} from "./ComponentAnatomySchematicSwitch";
import {
    TagSchematic,
    type TagSchematicContent,
} from "./ComponentAnatomySchematicTag";
import {
    TagsInputSchematic,
    type TagsInputSchematicContent,
} from "./ComponentAnatomySchematicTagsInput";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * @purpose X-ray of a component, drawn from plain elements that consume the
 * resolved token bindings directly. Nothing here is a live design-system
 * component, so switching interaction state is a data swap rather than a fight
 * with `:hover` / `:active` pseudo-classes — and every part stays individually
 * addressable for the legend's hover highlight.
 *
 * Geometry stays at the component's real px values and the whole subtree is
 * magnified with `zoom`, so token-driven radii and 1px borders scale honestly.
 * Each part is its own absolutely-positioned layer, which is what lets one part
 * be highlighted without dimming the ones nested inside it.
 */

const ControlSchematic = ({
    bindings,
    highlight,
    centre,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    centre: React.ReactNode;
}) => {
    const read = bindingReader(bindings);
    const controlSize = read("border", "width");
    const controlRadius = read("border", "border-radius");
    const outlineWidth = read("focusRing", "outline-width");
    const outlineColor = read("focusRing", "outline-color");

    return (
        <span
            className={classNames(styles.schematic, styles.controlSchematic)}
            style={{ columnGap: read("label", "column-gap") }}
        >
            <span
                className={styles.control}
                style={{ width: controlSize, height: controlSize }}
            >
                <Part
                    partId="halo"
                    highlight={highlight}
                    className={styles.halo}
                    style={{
                        inset: read("halo", "inset"),
                        background: read("halo", "background"),
                        borderRadius: read("halo", "border-radius"),
                    }}
                />
                {outlineWidth !== undefined && outlineColor !== undefined && (
                    <Part
                        partId="focusRing"
                        highlight={highlight}
                        className={styles.controlRing}
                        hitInset={`calc(-1 * ${outlineWidth})`}
                        style={{
                            borderRadius: controlRadius,
                            outline: `${outlineWidth} solid ${outlineColor}`,
                        }}
                    />
                )}
                <Part
                    partId="border"
                    highlight={highlight}
                    className={styles.controlBorder}
                    style={{
                        borderColor: read("border", "border-color"),
                        borderWidth: read("border", "border-width"),
                        borderRadius: controlRadius,
                    }}
                />
                <Part
                    partId="fill"
                    highlight={highlight}
                    className={styles.controlFill}
                    style={{
                        background: read("fill", "background"),
                        borderRadius: controlRadius,
                    }}
                />
                {centre}
            </span>
            <Part
                partId="label"
                highlight={highlight}
                className={styles.controlLabel}
                style={{
                    color: read("label", "color"),
                    fontSize: read("label", "font-size"),
                    fontWeight: read("label", "font-weight"),
                    lineHeight: read("label", "line-height"),
                    letterSpacing: read("label", "letter-spacing"),
                }}
            >
                Cash basis
            </Part>
        </span>
    );
};

const RadioSchematic = ({
    bindings,
    highlight,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
}) => {
    const read = bindingReader(bindings);
    const dotSize = read("dot", "width");

    return (
        <ControlSchematic
            bindings={bindings}
            highlight={highlight}
            centre={
                read("dot", "display") === "block" ? (
                    <Part
                        partId="dot"
                        highlight={highlight}
                        className={styles.radioDot}
                        style={{
                            width: dotSize,
                            height: dotSize,
                            background: read("dot", "background"),
                        }}
                    />
                ) : null
            }
        />
    );
};

const CheckboxSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);

    return (
        <ControlSchematic
            bindings={bindings}
            highlight={highlight}
            centre={
                read("glyph", "display") === "block" ? (
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        className={styles.checkboxGlyph}
                        hitInset="2px"
                        style={{ color: read("glyph", "color") }}
                    >
                        {variantId === "indeterminate" ? (
                            <IndeterminateGlyph />
                        ) : (
                            <CheckGlyph />
                        )}
                    </Part>
                ) : null
            }
        />
    );
};

type BindingOnlySchematicId = Extract<
    AnatomySchematic,
    "button" | "radio" | "otpField" | "menu"
>;

/** Schematics drawn from the resolved bindings alone, with no sandbox content. */
const bindingOnlySchematics: Record<
    BindingOnlySchematicId,
    (props: {
        bindings: readonly ResolvedAnatomyBinding[];
        highlight: AnatomyPartHighlight;
    }) => React.ReactNode
> = {
    button: ButtonSchematic,
    radio: RadioSchematic,
    otpField: OTPFieldSchematic,
    menu: MenuSchematic,
};

const isBindingOnlySchematic = (
    schematic: AnatomySchematic,
): schematic is BindingOnlySchematicId => schematic in bindingOnlySchematics;

// Schematics that draw from the bindings and the variant id alone.
const variantSchematics = {
    breadcrumbs: BreadcrumbsSchematic,
    checkbox: CheckboxSchematic,
    link: LinkSchematic,
    shortcut: ShortcutSchematic,
    sideNav: SideNavSchematic,
    tooltip: TooltipSchematic,
} satisfies Partial<
    Record<
        AnatomySchematic,
        React.ComponentType<{
            bindings: readonly ResolvedAnatomyBinding[];
            highlight: AnatomyPartHighlight;
            variantId: string;
        }>
    >
>;

type VariantSchematicId = keyof typeof variantSchematics;

const isVariantSchematic = (
    schematic: AnatomySchematic,
): schematic is VariantSchematicId =>
    Object.hasOwn(variantSchematics, schematic);

export const ComponentAnatomySchematic = ({
    schematic,
    variantId,
    bindings,
    highlight,
    tagContent,
    fieldContent,
    inputContent,
    textAreaContent,
    pickerContent,
    tagsInputContent,
    avatarContent,
    kickLogoContent,
    switchContent,
}: {
    schematic: AnatomySchematic;
    variantId: string;
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    /** Sandbox feature state the tag schematic mirrors, where the playground provides it. */
    tagContent?: TagSchematicContent;
    /** Sandbox content and feature state mirrored by the Field schematic. */
    fieldContent?: FieldSchematicContent;
    /** Sandbox feature state the input schematic mirrors, where the playground provides it. */
    inputContent?: InputSchematicContent;
    /** Sandbox feature state the text area schematic mirrors, where the playground provides it. */
    textAreaContent?: TextAreaSchematicContent;
    /** Sandbox feature state the picker schematic mirrors, where the playground provides it. */
    pickerContent?: PickerSchematicContent;
    /** Sandbox values and placeholder mirrored by the tags input schematic. */
    tagsInputContent?: TagsInputSchematicContent;
    /** Sandbox size mirrored by the avatar schematic. */
    avatarContent?: AvatarSchematicContent;
    /** Sandbox size mirrored by the KickLogo schematic. */
    kickLogoContent?: KickLogoSchematicContent;
    /** Sandbox label state mirrored by the switch schematic. */
    switchContent?: SwitchSchematicContent;
}) => {
    if (isBindingOnlySchematic(schematic)) {
        const BindingOnlySchematic = bindingOnlySchematics[schematic];
        return (
            <BindingOnlySchematic bindings={bindings} highlight={highlight} />
        );
    }

    if (isVariantSchematic(schematic)) {
        const Schematic = variantSchematics[schematic];
        return (
            <Schematic
                bindings={bindings}
                highlight={highlight}
                variantId={variantId}
            />
        );
    }

    switch (schematic) {
        case "tag":
            return (
                <TagSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={tagContent}
                />
            );
        case "input":
            return (
                <InputSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={inputContent}
                />
            );
        case "textArea":
            return (
                <TextAreaSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={textAreaContent}
                />
            );
        case "field":
            return (
                <FieldSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={fieldContent}
                />
            );
        case "picker":
            return (
                <PickerSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={pickerContent}
                />
            );
        case "tagsInput":
            return (
                <TagsInputSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={tagsInputContent}
                />
            );
        case "avatar":
            return (
                <AvatarSchematic
                    bindings={bindings}
                    highlight={highlight}
                    variantId={variantId}
                    content={avatarContent}
                />
            );
        case "kickLogo":
            return (
                <KickLogoSchematic
                    highlight={highlight}
                    variantId={variantId}
                    content={kickLogoContent}
                />
            );
        case "switch":
            return (
                <SwitchSchematic
                    bindings={bindings}
                    highlight={highlight}
                    content={switchContent}
                />
            );
    }
};
